import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const areaCodes = [{
  code: '253',
  src: 'https://cdn.magicpatterns.com/uploads/68rE1RFxKTaMLnZX9bTQ6m/253.png'
}, {
  code: '206',
  src: 'https://cdn.magicpatterns.com/uploads/6RRJ8Cb2MtYWCN2KCSKtBN/206.png'
}, {
  code: '425',
  src: 'https://cdn.magicpatterns.com/uploads/g4GXqv7wQ4H6HHSd1TBZ5s/425.png'
}];
const GRAVEL_SRC = 'https://cdn.magicpatterns.com/uploads/tqXEcbNCcYC2dBxg9zT8wG/GRAVEL.png';
// Cadence: 253 is the primary number and holds for HOLD_SECONDS. The
// alternate area codes (206, 425) don't hold — the odometer rolls right
// through them back to 253 in one continuous motion. Each individual
// roll takes TRANSITION_SECONDS. First cycle uses a short FIRST_HOLD so
// the rotation kicks off shortly after page load.
const HOLD_SECONDS = 80;
const TRANSITION_SECONDS = 1;
const FIRST_HOLD_SECONDS = 1.5;
interface LogoProps {
  className?: string;
  imgClassName?: string;
  invertOnLight?: boolean;
  /**
   * Multiplier on the hold time between rotations. 1 = base 80s hold (default).
   * 3 = 240s hold (used by footer for a slower cadence).
   */
  cycleEvery?: number;
}
/**
 * Logo lockup with a static "Gravel" wordmark layered on top of a rotating
 * area code. The area code rolls between values like a car odometer:
 * the outgoing number slides up and off, the incoming number slides up
 * into place. Mounts on a randomized area code so the page doesn't always
 * land on 253.
 */
export function Logo({
  className = '',
  imgClassName = 'h-10 w-auto',
  invertOnLight = true,
  cycleEvery = 1
}: LogoProps) {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * areaCodes.length));
  const [isFirstCycle, setIsFirstCycle] = useState(true);
  const current = areaCodes[index];
  useEffect(() => {
    let holdMs: number;
    if (isFirstCycle) {
      // Kick the first roll off shortly after page load, regardless of
      // which area code we randomly landed on.
      holdMs = FIRST_HOLD_SECONDS * 1000;
    } else if (current.code === '253') {
      // Primary number — hold here.
      holdMs = HOLD_SECONDS * 1000 * cycleEvery;
    } else {
      // 206 / 425 — no hold. Wait just long enough for the entry
      // animation to complete, then immediately roll to the next.
      holdMs = TRANSITION_SECONDS * 1000;
    }
    const timeout = setTimeout(() => {
      setIndex(i => (i + 1) % areaCodes.length);
      if (isFirstCycle) setIsFirstCycle(false);
    }, holdMs);
    return () => clearTimeout(timeout);
  }, [index, isFirstCycle, cycleEvery, current.code]);
  const invertClass = invertOnLight ? 'dark:brightness-100 brightness-0' : '';
  return <span className={`relative inline-block overflow-hidden ${className}`} aria-label={`${current.code}Gravel`} data-id="element-810">
      {/* Sizer: GRAVEL defines the layout dimensions and stays static on top */}
      <img src={GRAVEL_SRC} alt="" aria-hidden="true" className={`${imgClassName} ${invertClass} block relative z-10 pointer-events-none`} data-id="element-811" />

      {/* Rotating area code with odometer-style vertical roll. */}
      <AnimatePresence initial={false} data-id="element-812">
        <motion.img key={current.code} src={current.src} alt={current.code} className={`${imgClassName} ${invertClass} absolute inset-0 z-0`} initial={{
        y: '100%'
      }} animate={{
        y: '0%'
      }} exit={{
        y: '-100%'
      }} transition={{
        duration: TRANSITION_SECONDS,
        ease: [0.45, 0.05, 0.55, 0.95]
      }} data-id="element-813" />
      </AnimatePresence>
    </span>;
}